async function parseJson(response: Response) {
  return response.json().catch(() => null);
}

function extractErrorMessage(payload: any, fallback: string) {
  return payload?.error?.message || payload?.error || fallback;
}

export type UserContextResponse = {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    fullName: string;
    termsVersion: string | null;
    isPlatformAdmin: boolean;
    isFinancialAuditor?: boolean;
    integrationCapabilities?: string[];
  };
  activeOrganization: {
    id: string;
    name: string;
    logo: string | null;
    isCustomer: boolean;
    isAdmin: boolean;
  };
  organizations: Array<{
    id: string;
    name: string;
    logo: string | null;
    isCustomer: boolean;
    isAdmin: boolean;
  }>;
  availableOrganizations: Array<{
    id: string;
    name: string;
    logo: string | null;
    isCustomer: boolean;
  }>;
  terms: {
    currentVersion: string;
    accepted: boolean;
  };
  publicOrganizationId: string;
};

export async function getCurrentUserContext() {
  const response = await fetch("/api/v1/users/me", {
    method: "GET",
    credentials: "same-origin",
    cache: "no-store",
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    const error = new Error(
      extractErrorMessage(payload, "Failed to load user context"),
    ) as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }

  return payload?.data as UserContextResponse;
}

export async function inviteUser(input: {
  name: string;
  email: string;
  isAdmin: boolean;
  organizationId: string;
}) {
  const response = await fetch("/api/v1/users/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to send invitation"));
  }

  return payload?.data;
}

export async function acceptUserInvitation(input: {
  invitationId: string;
  email: string;
  firstName: string;
  lastName: string;
  signupSlug: string;
  organizationId: string;
}) {
  const response = await fetch("/api/v1/users/invite/accept", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to accept invitation"));
  }

  return payload?.data;
}

export async function resendUserInvitation(input: {
  invitationId: string;
  organizationId: string;
}) {
  const response = await fetch(`/api/v1/users/invite/${input.invitationId}/resend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ organizationId: input.organizationId }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to resend invitation"));
  }

  return payload?.data;
}

export async function deleteUserInvitation(input: {
  invitationId: string;
  organizationId: string;
}) {
  const params = new URLSearchParams({ organizationId: input.organizationId });
  const response = await fetch(`/api/v1/users/invite/${input.invitationId}?${params.toString()}`, {
    method: "DELETE",
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to delete invitation"));
  }

  return payload?.data;
}

export async function softDeleteUser(input: {
  userId: string;
  organizationId: string;
  reason?: string;
}) {
  const params = new URLSearchParams({ organizationId: input.organizationId });
  if (input.reason?.trim()) {
    params.set("reason", input.reason.trim());
  }
  const response = await fetch(`/api/v1/users/${input.userId}?${params.toString()}`, {
    method: "DELETE",
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to delete user"));
  }

  return payload?.data;
}

export async function updateUser(input: {
  userId: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  isPlatformAdmin?: boolean;
  isFinancialAuditor?: boolean;
  integrationRoleKeys?: string[];
  organizationIds?: string[];
  memberships?: Array<{ organizationId: string; isAdmin: boolean }>;
}) {
  const params = new URLSearchParams({ organizationId: input.organizationId });
  const response = await fetch(`/api/v1/users/${input.userId}?${params.toString()}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email,
      isAdmin: input.isAdmin,
      isPlatformAdmin: input.isPlatformAdmin,
      isFinancialAuditor: input.isFinancialAuditor,
      integrationRoleKeys: input.integrationRoleKeys,
      organizationIds: input.organizationIds,
      memberships: input.memberships,
    }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to update user"));
  }

  return payload?.data;
}

export async function acceptTerms(version: string) {
  const response = await fetch("/api/v1/users/me/terms", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to accept terms"));
  }

  return payload?.data;
}

export async function changeMyPassword(input: { currentPassword?: string; newPassword: string }) {
  const response = await fetch("/api/v1/users/me/password", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to change password"));
  }

  return payload?.data;
}

export async function addPublicAccountUser(input: {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
}) {
  const response = await fetch("/api/v1/users/public-account", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to create public account user"));
  }

  return payload?.data;
}

export async function listUsersWithInvitations(input: {
  organizationId: string;
  includeAllOrganizations?: boolean;
}) {
  const params = new URLSearchParams({ organizationId: input.organizationId });
  if (input.includeAllOrganizations) {
    params.set("includeAllOrganizations", "true");
  }
  const response = await fetch(`/api/v1/users?${params.toString()}`);
  const payload = await parseJson(response);
  if (!response.ok) {
    const error = new Error(extractErrorMessage(payload, "Failed to load users")) as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }

  return payload?.data || [];
}

export async function impersonateUser(userId: string) {
  const response = await fetch(`/api/v1/users/${userId}/impersonate`, {
    method: "POST",
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, "Failed to impersonate user"));
  }

  return payload?.data as {
    token: string;
    user: {
      id: string;
      email: string | null;
      firstName: string | null;
      lastName: string | null;
    };
  };
}
